import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { hasPermission } from '../../lib/permissions';
import { useToast } from '../../components/ui/Toast';
import { generateSharingUrl } from '../../lib/utils';
import toast from 'react-hot-toast';
import logger from '../../utils/logger';
import {
  ArrowLeftIcon,
  UserPlusIcon,
  LinkIcon,
  ClipboardDocumentIcon,
  CheckIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  EnvelopeIcon,
  ShareIcon,
  DocumentDuplicateIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';

const AssessmentInvite = ({
  isModal = false,
  assessmentId: propAssessmentId,
  jobPostingId: propJobPostingId,
  assessmentFormData: propAssessmentFormData,
  onDone,
  registerFinish,
  saveQuestionsFn,
  onBack,
}) => {
  const { id: routeId } = useParams();
  const assessmentId = propAssessmentId || routeId;
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { success, error } = useToast();

  const canView = hasPermission(user, 'assessment.view');
  const canInvite = hasPermission(user, 'assessment.invite');

  useEffect(() => {
    if (user && (!canView || !canInvite)) {
      navigate('/dashboard/assessments', { replace: true });
    }
  }, [user, canView, canInvite, navigate]);

  // Check if coming from HireLoop
  const returnTo = location.state?.returnTo;
  const fromHireLoop = location.state?.fromHireLoop || false;
  const jobPostingId = propJobPostingId || location.state?.jobPostingId;

  const [assessment, setAssessment] = useState(null);
  const [loading, setLoading] = useState(false); // Start with false for modal flow - assessment will be created on final step
  const [sharingLink, setSharingLink] = useState('');
  const [copied, setCopied] = useState(false);
  const [finishing, setFinishing] = useState(false);
  const [emailPreview, setEmailPreview] = useState('');

  // Use ref to store registerFinish callback to avoid infinite loops
  const registerFinishRef = useRef(registerFinish);
  useEffect(() => {
    registerFinishRef.current = registerFinish;
  }, [registerFinish]);
  const [emailPreviewEdited, setEmailPreviewEdited] = useState(false);
  const emailPreviewEditedRef = useRef(false);
  // Invite settings matching Interview flow
  const [activeSharePlatform, setActiveSharePlatform] = useState('email');
  const [inviteExpiry, setInviteExpiry] = useState('1-week');
  const [emailReminderFrequency, setEmailReminderFrequency] = useState('none');
  // Job posting details for email preview (similar to Interview flow)
  const [jobPostingDetails, setJobPostingDetails] = useState(null);

  // Define fetchAssessment using useCallback
  const fetchAssessment = useCallback(async () => {
    if (!assessmentId) return;
    try {
      setLoading(true);
      const response = await axios.get(`/api/assessments/${assessmentId}`);
      setAssessment(response.data.assessment);

      // Generate sharing link - use existing one if available, or generate a preview
      // The link will work once the assessment is published (which happens on Finish & Publish)
      let linkPath = `/assessment/${response.data.assessment.sharingLink || assessmentId}`;
      const finalSharingLink = generateSharingUrl(linkPath);

      logger.log('🔗 Sharing link generated:', finalSharingLink);

      setSharingLink(finalSharingLink);

      // Generate email preview will be handled by generateEmailPreview function
    } catch (err) {
      console.error('Error fetching assessment:', err);
      if (!isModal) {
        error('Error', 'Failed to load assessment');
        navigate('/dashboard/assessments');
      }
    } finally {
      setLoading(false);
    }
  }, [assessmentId, isModal, navigate, error]);

  // Fetch job posting details for email preview (similar to Interview flow)
  const fetchJobPostingDetails = useCallback(async () => {
    if (!jobPostingId) return;
    try {
      const response = await axios.get(`/api/job-posts/jobs/${jobPostingId}`);
      setJobPostingDetails(response.data.job);
    } catch (error) {
      console.error('Error fetching job posting details:', error);
      // Don't block if job posting fetch fails
    }
  }, [jobPostingId]);

  // Fetch job posting details when component mounts
  useEffect(() => {
    if (jobPostingId) {
      fetchJobPostingDetails();
    }
  }, [jobPostingId, fetchJobPostingDetails]);

  useEffect(() => {
    // Fetch assessment when assessmentId changes (e.g., after it's created in final step)
    if (assessmentId) {
      fetchAssessment();
    } else if (isModal && !assessmentId) {
      // In modal flow without assessmentId, we're ready to show the invite UI
      // It will be populated when assessment is created on "Finish & Publish"
      setLoading(false);
    }
  }, [assessmentId, isModal, fetchAssessment]);

  // Generate email preview based on assessment data (similar to Interview flow)
  const generateEmailPreview = useCallback(() => {
    // Use assessment data if available, otherwise fall back to assessmentFormData from modal
    const assessmentTitle =
      assessment?.title || propAssessmentFormData?.title || '[Assessment Title]';
    const candidateName = '[Candidate Name]';
    const inviteLink = sharingLink || '[Assessment Link]';
    const jobTitle = assessment?.jobPosting?.title || jobPostingDetails?.title || '[Position]';

    const expiryText =
      inviteExpiry === '1-week'
        ? '7 days'
        : inviteExpiry === '2-weeks'
          ? '14 days'
          : inviteExpiry === '1-month'
            ? '30 days'
            : inviteExpiry === '2-months'
              ? '60 days'
              : '90 days';

    const duration = assessment?.duration || propAssessmentFormData?.duration || '[Duration]';

    const preview = `Subject: Assessment Invitation - ${jobTitle}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
📝 Assessment Invitation
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

Hi ${candidateName},

Thank you for your interest! We'd like to invite you to take an assessment for the ${jobTitle} position.

📋 ASSESSMENT DETAILS
────────────────────
Assessment: ${assessmentTitle}
Position: ${jobTitle}
Duration: ${duration} minutes
Valid Until: ${expiryText} from now

🚀 START YOUR ASSESSMENT
────────────────────
Click the link below to begin:
${inviteLink}

💡 TIPS FOR SUCCESS
────────────────────
• Find a quiet space with stable internet
• Read each question carefully
• Take your time - there's no need to rush
• Be honest and authentic in your responses

📞 NEED HELP?
────────────────────
If you have any questions or technical issues, please reply to this email.

Best regards,
The Hiring Team

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Powered by TalentCrux Assessment Platform
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`;

    return preview;
  }, [
    assessment?.title,
    assessment?.duration,
    assessment?.jobPosting?.title,
    propAssessmentFormData?.title,
    propAssessmentFormData?.duration,
    jobPostingDetails?.title,
    sharingLink,
    inviteExpiry,
  ]);

  // Initialize email preview on mount (always, like Interview flow)
  useEffect(() => {
    const initialPreview = generateEmailPreview();
    setEmailPreview(initialPreview);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Update email preview when key fields change (only if user hasn't edited)
  useEffect(() => {
    // Only auto-update if user hasn't manually edited
    if (!emailPreviewEditedRef.current) {
      setEmailPreview(generateEmailPreview());
    }
  }, [
    assessment?.title,
    assessment?.duration,
    propAssessmentFormData?.title,
    propAssessmentFormData?.duration,
    jobPostingDetails?.title,
    sharingLink,
    inviteExpiry,
    generateEmailPreview,
  ]);

  const copyToClipboard = async () => {
    logger.log('🔗 Copy to clipboard initiated for:', sharingLink);
    logger.log('🌐 Navigator clipboard available:', !!navigator.clipboard);
    logger.log('📱 User agent:', navigator.userAgent);

    try {
      // Try modern Clipboard API first
      if (navigator.clipboard && navigator.clipboard.writeText) {
        logger.log('✅ Using modern Clipboard API');
        await navigator.clipboard.writeText(sharingLink);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
        success('Success', 'Link copied to clipboard!');
        logger.log('✅ Modern API copy successful');
      } else {
        logger.log('🔄 Modern API not available, using fallback method');
        // Fallback for older browsers/devices
        const textArea = document.createElement('textarea');
        textArea.value = sharingLink;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();

        try {
          const fallbackResult = document.execCommand('copy');
          logger.log('📋 Fallback copy result:', fallbackResult);
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
          success('Success', 'Link copied to clipboard!');
        } catch (fallbackErr) {
          logger.log('⚠️ Fallback method failed:', fallbackErr);
          // If even fallback fails, show the link for manual copy
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
          success('Success', 'Link copied to clipboard!');
        } finally {
          document.body.removeChild(textArea);
        }
      }
    } catch (err) {
      console.error('❌ Copy to clipboard error:', err);
      // If all methods fail, show the link for manual copy
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      success('Success', 'Link copied to clipboard!');
    }
  };

  const handleInviteLater = () => {
    if (isModal) {
      if (typeof onDone === 'function') onDone();
      return;
    }
    if (fromHireLoop) {
      // Navigate back to HireLoop if coming from there
      const target = '/dashboard/hireloop';
      const linkedJobId = jobPostingId || assessment?.jobPosting;
      navigate(target, {
        state: {
          assessmentCreated: true,
          jobPostingId: linkedJobId,
          screeningChoice: location.state?.screeningChoice,
          jobData: location.state?.jobData,
        },
      });
    } else {
      // Default: navigate to assessments list
      navigate('/dashboard/assessments');
    }
  };

  // Handle share platform selection
  const handleSharePlatformChange = (platform) => {
    setActiveSharePlatform(platform);
  };

  // Register finish handler for modal - will be called after assessment is created and published
  // Use ref to avoid infinite loops when parent function changes
  useEffect(() => {
    if (isModal) {
      // Use requestAnimationFrame to defer registration until after render
      const frameId = requestAnimationFrame(() => {
        if (typeof registerFinishRef.current === 'function') {
          registerFinishRef.current(() => async (newAssessmentId) => {
            // Finish handler - refresh assessment data after publishing
            // newAssessmentId is the newly created assessment ID
            const finalId = newAssessmentId || assessmentId;
            if (!finalId) {
              return true;
            }

            try {
              // Update assessmentId state if it's new
              if (newAssessmentId && newAssessmentId !== assessmentId) {
                // AssessmentId will be updated by parent modal
              }

              const response = await axios.get(`/api/assessments/${finalId}`);
              setAssessment(response.data.assessment);

              // Update sharing link if it was generated during publish
              if (response.data.assessment.sharingLink) {
                const updatedLink = generateSharingUrl(
                  `/assessment/${response.data.assessment.sharingLink}`
                );
                setSharingLink(updatedLink);

                // Email preview will be auto-updated by generateEmailPreview effect
              }
            } catch (err) {
              console.error('Error refreshing assessment after publish:', err);
            }

            return true;
          });
        }
      });
      return () => cancelAnimationFrame(frameId);
    }
  }, [isModal, assessmentId]);

  if (loading) {
    return (
      <div className={isModal ? '' : 'min-h-screen bg-gray-50 py-8'}>
        <div className={isModal ? 'max-w-none px-0' : 'max-w-4xl mx-auto px-4 sm:px-6 lg:px-8'}>
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
            <div className="h-64 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  // In modal flow, allow rendering even without assessment - it will be populated after "Finish & Publish"
  // Only show error in non-modal flow when assessmentId exists but assessment is missing
  if (!assessment && assessmentId && !isModal) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Assessment not found</h1>
            <button
              onClick={() => navigate('/dashboard/assessments')}
              className="text-indigo-600 hover:text-indigo-500"
            >
              Back to Assessments
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={isModal ? '' : 'min-h-screen bg-gray-50 py-8'}>
      <div className={isModal ? 'max-w-none px-0' : 'max-w-4xl mx-auto px-4 sm:px-6 lg:px-8'}>
        {!isModal && (
          <>
            {/* Header */}
            <div className="mb-8">
              {/* Back button - only show if not from HireLoop */}
              {!fromHireLoop && (
                <button
                  onClick={() => navigate('/dashboard/assessments')}
                  className="flex items-center text-sm text-gray-600 hover:text-gray-900 mb-4"
                >
                  <ArrowLeftIcon className="h-4 w-4 mr-2" />
                  Back to Assessments
                </button>
              )}

              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-3xl font-bold text-gray-900">Invite Candidates</h1>
                  <p className="text-gray-600 mt-2">
                    Invite candidates to take your assessment
                    {assessment?.title ? `: ${assessment.title}` : ''}
                  </p>
                </div>

                {assessment && (
                  <span className="px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                    Published
                  </span>
                )}
              </div>
            </div>
          </>
        )}

        {/* Share Options - Matching Interview Invite Flow */}
        <div className="space-y-5">
          {/* Share Options - Smaller Grid */}
          <div className="grid grid-cols-4 gap-3">
            {/* Email */}
            <button
              type="button"
              onClick={() => handleSharePlatformChange('email')}
              className={`p-3 rounded-lg border-2 transition-all text-center ${
                activeSharePlatform === 'email'
                  ? 'border-purple-500 bg-purple-50'
                  : 'border-gray-200 hover:border-gray-300 bg-white'
              }`}
            >
              <div className="w-10 h-10 bg-purple-600 rounded-lg flex items-center justify-center mx-auto mb-2">
                <EnvelopeIcon className="w-5 h-5 text-white" />
              </div>
              <p className="text-xs font-medium text-gray-900">Email</p>
            </button>

            {/* WhatsApp */}
            <button
              type="button"
              onClick={() => handleSharePlatformChange('whatsapp')}
              className={`p-3 rounded-lg border-2 transition-all text-center ${
                activeSharePlatform === 'whatsapp'
                  ? 'border-green-500 bg-green-50'
                  : 'border-gray-200 hover:border-gray-300 bg-white'
              }`}
            >
              <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center mx-auto mb-2">
                <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                </svg>
              </div>
              <p className="text-xs font-medium text-gray-900">WhatsApp</p>
            </button>

            {/* LinkedIn */}
            <button
              type="button"
              onClick={() => handleSharePlatformChange('linkedin')}
              className={`p-3 rounded-lg border-2 transition-all text-center ${
                activeSharePlatform === 'linkedin'
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300 bg-white'
              }`}
            >
              <div className="w-10 h-10 bg-blue-700 rounded-lg flex items-center justify-center mx-auto mb-2">
                <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                </svg>
              </div>
              <p className="text-xs font-medium text-gray-900">LinkedIn</p>
            </button>

            {/* Copy Link */}
            <button
              type="button"
              onClick={() => handleSharePlatformChange('link')}
              className={`p-3 rounded-lg border-2 transition-all text-center ${
                activeSharePlatform === 'link'
                  ? 'border-gray-500 bg-gray-100'
                  : 'border-gray-200 hover:border-gray-300 bg-white'
              }`}
            >
              <div className="w-10 h-10 bg-gray-600 rounded-lg flex items-center justify-center mx-auto mb-2">
                <LinkIcon className="w-5 h-5 text-white" />
              </div>
              <p className="text-xs font-medium text-gray-900">Copy Link</p>
            </button>
          </div>

          {/* Platform-specific Preview */}
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            {/* Email Preview */}
            {(activeSharePlatform === 'email' || !activeSharePlatform) && (
              <div className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <EnvelopeIcon className="w-4 h-4 text-purple-600" />
                    <span className="text-sm font-medium text-gray-700">Email Preview</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      navigator.clipboard.writeText(emailPreview);
                      toast.success('Email copied to clipboard!');
                    }}
                    className="flex items-center gap-1 px-2 py-1 text-xs text-purple-600 hover:bg-purple-50 rounded transition-colors"
                  >
                    <DocumentDuplicateIcon className="w-3.5 h-3.5" />
                    Copy
                  </button>
                </div>
                <textarea
                  value={emailPreview}
                  onChange={(e) => {
                    emailPreviewEditedRef.current = true;
                    setEmailPreview(e.target.value);
                  }}
                  rows={16}
                  className="w-full px-3 py-2 border border-gray-200 rounded-md text-xs font-mono focus:outline-none focus:ring-1 focus:ring-purple-500 resize-none bg-gray-50"
                  placeholder="Email preview..."
                />
                {/* Invite Settings */}
                <div className="mt-3 pt-3 border-t border-gray-100 space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1.5">
                        Invite Link Expiry
                      </label>
                      <select
                        value={inviteExpiry}
                        onChange={(e) => setInviteExpiry(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-purple-500"
                      >
                        <option value="1-week">1 Week (Default)</option>
                        <option value="2-weeks">2 Weeks</option>
                        <option value="1-month">1 Month</option>
                        <option value="2-months">2 Months</option>
                        <option value="3-months">3 Months</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1.5">
                        Email Reminder Frequency
                      </label>
                      <select
                        value={emailReminderFrequency}
                        onChange={(e) => setEmailReminderFrequency(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-purple-500"
                      >
                        <option value="none">No Reminders</option>
                        <option value="daily">Daily</option>
                        <option value="every-2-days">Every 2 Days</option>
                        <option value="every-3-days">Every 3 Days</option>
                        <option value="weekly">Weekly</option>
                      </select>
                    </div>
                  </div>
                  {emailReminderFrequency !== 'none' && (
                    <p className="text-xs text-gray-500 bg-purple-50 p-2 rounded">
                      📧 Reminder emails will be sent{' '}
                      {emailReminderFrequency === 'daily'
                        ? 'every day'
                        : emailReminderFrequency === 'every-2-days'
                          ? 'every 2 days'
                          : emailReminderFrequency === 'every-3-days'
                            ? 'every 3 days'
                            : 'every week'}{' '}
                      until the candidate completes the assessment or the link expires.
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* WhatsApp Preview */}
            {activeSharePlatform === 'whatsapp' && (
              <div className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                  </svg>
                  <span className="text-sm font-medium text-gray-700">WhatsApp Draft</span>
                </div>
                <div className="bg-green-50 rounded-lg p-3 border border-green-100">
                  <p className="text-sm text-gray-700 whitespace-pre-line">{`Hi [Candidate Name]! 👋

You've been invited to take an assessment for: ${assessment?.title || '[Assessment Title]'}

🔗 Start here: ${sharingLink || '[Assessment Link]'}

⏰ Please complete within ${inviteExpiry === '1-week' ? '1 week' : inviteExpiry === '2-weeks' ? '2 weeks' : inviteExpiry === '1-month' ? '1 month' : inviteExpiry === '2-months' ? '2 months' : '3 months'}

Good luck! 🍀`}</p>
                </div>
                <p className="mt-2 text-xs text-gray-500">Copy the draft above to share via WhatsApp</p>
              </div>
            )}

            {/* LinkedIn Preview */}
            {activeSharePlatform === 'linkedin' && (
              <div className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  <svg className="w-4 h-4 text-blue-700" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                  </svg>
                  <span className="text-sm font-medium text-gray-700">LinkedIn InMail Draft</span>
                </div>
                <div className="bg-blue-50 rounded-lg p-3 border border-blue-100">
                  <p className="text-sm font-medium text-gray-900 mb-2">
                    Subject: Assessment Invitation - {assessment?.title || '[Assessment Title]'}
                  </p>
                  <p className="text-sm text-gray-700 whitespace-pre-line">{`Hi [Candidate Name],

I hope this message finds you well. We're impressed with your profile and would like to invite you to take an assessment for the position mentioned above.

Assessment Link: ${sharingLink || '[Link]'}

This assessment will evaluate your qualifications and should take approximately ${assessment?.duration || '[Duration]'} minutes. Please complete it at your convenience within the next ${inviteExpiry === '1-week' ? 'week' : inviteExpiry === '2-weeks' ? 'two weeks' : inviteExpiry === '1-month' ? 'month' : inviteExpiry === '2-months' ? 'two months' : 'three months'}.

Best regards,
[Your Name]`}</p>
                </div>
                <p className="mt-2 text-xs text-gray-500">LinkedIn integration coming soon</p>
              </div>
            )}

            {/* Copy Link Preview */}
            {activeSharePlatform === 'link' && (
              <div className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  <LinkIcon className="w-4 h-4 text-gray-600" />
                  <span className="text-sm font-medium text-gray-700">Assessment Link</span>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    readOnly
                    value={sharingLink || '[Link will be generated after creation]'}
                    className="flex-1 px-3 py-2 border border-gray-200 rounded-md text-sm bg-gray-50 text-gray-500"
                  />
                  <button
                    type="button"
                    className="px-3 py-2 bg-gray-600 text-white rounded-md text-sm hover:bg-gray-700 flex items-center gap-1"
                    onClick={() => {
                      if (sharingLink) {
                        navigator.clipboard.writeText(sharingLink);
                        toast.success('Link copied to clipboard!');
                      } else {
                        toast.info('Link will be available after creating the assessment');
                      }
                    }}
                  >
                    <DocumentDuplicateIcon className="w-4 h-4" />
                    Copy
                  </button>
                </div>
                <p className="mt-2 text-xs text-gray-500">
                  Share this link anywhere - social media, messaging apps, etc.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Current Invitations - Only show if not in modal */}
        {!isModal && assessment?.invitedCandidates && assessment.invitedCandidates.length > 0 && (
          <div className="border-t border-gray-200 pt-6">
            <h3 className="text-sm font-medium text-gray-700 mb-4">Current Invitations</h3>
            <div className="space-y-2">
              {assessment.invitedCandidates.map((candidate, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                >
                  <div>
                    <div className="font-medium text-gray-900 text-sm">
                      {candidate.name || 'No name provided'}
                    </div>
                    <div className="text-xs text-gray-600">{candidate.email}</div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${
                        candidate.status === 'invited'
                          ? 'bg-blue-100 text-blue-800'
                          : candidate.status === 'started'
                            ? 'bg-yellow-100 text-yellow-800'
                            : candidate.status === 'completed'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-800'
                      }`}
                      title={
                        candidate.status === 'completed' && candidate.completedAt
                          ? `Completed on ${new Date(candidate.completedAt).toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}`
                          : candidate.status === 'started' && candidate.startedAt
                          ? `Started on ${new Date(candidate.startedAt).toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}`
                          : candidate.invitedAt
                          ? `Invited on ${new Date(candidate.invitedAt).toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}`
                          : undefined
                      }
                    >
                      {candidate.status.charAt(0).toUpperCase() + candidate.status.slice(1)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AssessmentInvite;
